import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useSeo } from '@/hooks/useSeo';

const VALUES = [
  {
    icon: 'ri-time-line',
    title: '资源更新及时',
    desc: '按更新时间倒序整理影视资源，让你第一时间发现刚上线的电影与电视剧。',
  },
  {
    icon: 'ri-newspaper-line',
    title: '资讯内容丰富',
    desc: '覆盖国内资讯、国际资讯、影人报道与行业观察，多角度呈现影视行业动态。',
  },
  {
    icon: 'ri-layout-masonry-line',
    title: '浏览体验友好',
    desc: '分类浏览、关键词搜索与多维筛选相结合，电脑端与手机端均可舒适访问。',
  },
  {
    icon: 'ri-shield-check-line',
    title: '内容清晰可靠',
    desc: '页面结构清晰、信息层级分明，所有影视信息与资讯均统一整理，易于阅读。',
  },
];

export default function AboutPage() {
  useSeo({
    title: '关于我们 - 91爱爱影视平台',
    description:
      '了解91爱爱影视资讯平台的定位、内容范围与联系方式。91爱爱聚焦最新影视资源与影视资讯，为影视爱好者提供便捷的浏览体验。',
    keywords: '91爱爱,关于我们,影视平台介绍,联系方式,权益声明',
  });

  return (
    <SiteLayout>
      <div className="mx-auto max-w-[1280px] px-4 pt-6 md:px-6">
        <Breadcrumb items={[{ label: '关于我们' }]} />
      </div>

      <section className="mx-auto max-w-[1280px] px-4 py-6 md:px-6">
        <div className="overflow-hidden rounded-lg border border-background-200">
          <div className="relative">
            <div className="relative h-[240px] w-full md:h-[300px]">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20cinema%20hall%20interior%20with%20rows%20of%20deep%20navy%20seats%20and%20warm%20amber%20accent%20lighting%2C%20soft%20bokeh%20screen%20glow%20in%20the%20background%2C%20calm%20premium%20editorial%20mood%2C%20high%20detail&width=1600&height=600&seq=about-hero-91aa&orientation=landscape"
                alt="91爱爱影视平台"
                title="91爱爱影视资讯平台"
                className="absolute inset-0 h-full w-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/60 to-foreground-950/30" />
            </div>
            <div className="absolute inset-0 flex items-center">
              <div className="px-6 md:px-10">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-background-50/15 px-3 py-1 text-xs text-background-100 backdrop-blur">
                  <i className="ri-movie-2-line" />
                  关于我们
                </span>
                <h1 className="mt-4 font-heading text-2xl font-bold text-background-50 md:text-4xl">
                  91爱爱 · 最新影视资源与影视资讯平台
                </h1>
                <p className="mt-3 max-w-2xl text-sm leading-relaxed text-background-200 md:text-base">
                  我们相信，好的平台不只是资源的集合，更是一种清晰、舒适的发现方式。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1280px] px-4 pb-4 md:px-6">
        <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
          <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
            <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
              <i className="ri-compass-3-line text-primary-600" />
              网站定位
            </h2>
            <div className="mt-4 flex flex-col gap-4 text-sm leading-7 text-foreground-600 md:text-base">
              <p>
                91爱爱聚焦最新影视资源与影视资讯，目标用户是热爱电影、电视剧及影视相关内容的普通观众与影视爱好者。
                平台希望满足用户在查找、浏览影视作品以及了解行业动态方面的核心需求。
              </p>
              <p>
                在内容结构上，网站涵盖影片分类浏览、影视资源详情与综合资讯报道三大板块，重点突出资源更新及时与资讯权威丰富。
                通过首页焦点推荐、分类快捷入口、最新影视列表与资讯精选，用户可以快速进入感兴趣的内容。
              </p>
              <p>
                本站为演示站点，所有影视信息、评分、导演、演员与资讯内容均为虚构示例，仅用于展示平台结构与交互方式。
              </p>
            </div>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {VALUES.map((item) => (
                <div key={item.title} className="rounded-lg border border-background-200 bg-background-100/60 p-4">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                    <i className={`${item.icon} text-xl`} />
                  </span>
                  <h3 className="mt-3 font-heading text-base font-semibold text-foreground-950">
                    {item.title}
                  </h3>
                  <p className="mt-1.5 text-xs leading-relaxed text-foreground-500">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-6">
            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-customer-service-2-line text-primary-600" />
                联系方式
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                如果你对平台内容有建议、发现信息有误，或希望与我们合作，欢迎通过以下方式联系：
              </p>
              <ul className="mt-4 flex flex-col gap-4 text-sm">
                <li className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                    <i className="ri-mail-line" />
                  </span>
                  <div>
                    <p className="font-medium text-foreground-900">邮箱</p>
                    <p className="text-xs text-foreground-500">contact@91aiai.example.com</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                    <i className="ri-phone-line" />
                  </span>
                  <div>
                    <p className="font-medium text-foreground-900">客服电话</p>
                    <p className="text-xs text-foreground-500">400-000-9191（工作日 9:00-18:00）</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                    <i className="ri-map-pin-line" />
                  </span>
                  <div>
                    <p className="font-medium text-foreground-900">办公地点</p>
                    <p className="text-xs text-foreground-500">演示地址，仅用于展示示例</p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="rounded-lg border border-accent-200 bg-accent-50 p-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-accent-900">
                <i className="ri-copyright-line" />
                权益声明
              </h2>
              <ul className="mt-3 flex flex-col gap-2 text-xs leading-relaxed text-accent-900/85">
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5" />
                  本站不提供任何影视播放、下载与存储服务。
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5" />
                  站内所有影视与资讯内容均为演示示例，不构成真实推荐。
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5" />
                  如有内容涉及权利问题，可通过上述方式与我们联系处理。
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1280px] px-4 py-8 md:px-6">
        <div className="flex flex-col items-center justify-between gap-5 rounded-lg border border-background-200 bg-background-100/70 p-6 md:flex-row md:p-8">
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-950">
              开始探索 91爱爱 的影视资源
            </h2>
            <p className="mt-1.5 text-sm text-foreground-600">
              浏览最新影视资源，或阅读影视行业资讯，找到属于你的下一部作品。
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/category/hot"
              className="flex h-11 items-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-6 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700 cursor-pointer"
            >
              <i className="ri-fire-line" />
              浏览热门推荐
            </Link>
            <Link
              to="/news"
              className="flex h-11 items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 cursor-pointer"
            >
              <i className="ri-newspaper-line" />
              阅读影视资讯
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}